import { useMemo, useState } from "react";
import { ItemAggregate } from "./types";
import { num, usd } from "./helpers";
import { ItemDetails } from "./ItemDetails";

type SortField = "posName" | "vendorName" | "productType" | "totalNetCents" | "totalUnitsSold" | "totalGrossCents";
type SortDir = "asc" | "desc";

interface Props {
  items: ItemAggregate[];
  caption?: string;
}

const columns: { field: SortField; label: string; numeric?: boolean; hideOn?: string }[] = [
  { field: "posName", label: "Product" },
  { field: "vendorName", label: "Vendor", hideOn: "sm" },
  { field: "productType", label: "Type", hideOn: "lg" },
  { field: "totalNetCents", label: "Net Sales", numeric: true },
  { field: "totalUnitsSold", label: "Units", numeric: true },
  { field: "totalGrossCents", label: "Gross", numeric: true, hideOn: "md" },
];

const hideClass = (hideOn?: string) => {
  switch (hideOn) {
    case "sm":
      return "hidden sm:table-cell";
    case "md":
      return "hidden md:table-cell";
    case "lg":
      return "hidden lg:table-cell";
    default:
      return "";
  }
};

export const ItemTable = ({ items, caption }: Props) => {
  const [sortField, setSortField] = useState<SortField>("totalNetCents");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const clickHeader = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(field === "posName" || field === "vendorName" || field === "productType" ? "asc" : "desc");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.posName.toLowerCase().includes(q) ||
        i.vendorName.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q) ||
        i.productType.toLowerCase().includes(q) ||
        i.upc.toLowerCase().includes(q)
    );
  }, [items, query]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      return sortDir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return list;
  }, [filtered, sortField, sortDir]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="min-w-0">
          {caption && <div className="text-sm font-semibold text-gray-800 truncate">{caption}</div>}
          <div className="text-xs text-gray-500">
            {sorted.length.toLocaleString()} of {items.length.toLocaleString()} item{items.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="relative w-48 sm:w-64 flex-shrink-0">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter items…"
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
          />
          <svg
            className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-white border-b border-gray-200">
              <th className="w-8 px-2 py-2" />
              {columns.map((col) => (
                <th
                  key={col.field}
                  onClick={() => clickHeader(col.field)}
                  className={`px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold cursor-pointer select-none hover:text-gray-800 ${
                    col.numeric ? "text-right" : "text-left"
                  } ${hideClass(col.hideOn)}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    <SortIcon active={sortField === col.field} dir={sortDir} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-gray-400 text-sm">
                  No items match "{query}".
                </td>
              </tr>
            )}
            {sorted.map((item) => {
              const isOpen = expanded.has(item.key);
              return (
                <ExpandableRow
                  key={item.key}
                  item={item}
                  isOpen={isOpen}
                  onToggle={() => toggle(item.key)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ExpandableRow = ({
  item,
  isOpen,
  onToggle,
}: {
  item: ItemAggregate;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <>
    <tr
      onClick={onToggle}
      className={`cursor-pointer transition-colors ${isOpen ? "bg-red-50/40" : "hover:bg-gray-50"}`}
    >
      <td className="px-2 py-2.5 align-middle">
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </td>
      <td className="px-3 py-2.5 align-middle">
        <div className="font-medium text-gray-900">{item.posName || "(unnamed)"}</div>
        <div className="sm:hidden text-xs text-gray-500">{item.vendorName || "—"}</div>
      </td>
      <td className={`px-3 py-2.5 align-middle text-gray-700 ${hideClass("sm")}`}>{item.vendorName || "—"}</td>
      <td className={`px-3 py-2.5 align-middle text-gray-700 ${hideClass("lg")}`}>{item.productType || "—"}</td>
      <td className="px-3 py-2.5 align-middle text-right tabular-nums font-semibold text-green-700">
        {usd(item.totalNetCents)}
      </td>
      <td className="px-3 py-2.5 align-middle text-right tabular-nums text-gray-800">{num(item.totalUnitsSold)}</td>
      <td className={`px-3 py-2.5 align-middle text-right tabular-nums text-gray-700 ${hideClass("md")}`}>
        {usd(item.totalGrossCents)}
      </td>
    </tr>
    {isOpen && (
      <tr>
        <td colSpan={columns.length + 1} className="p-0">
          <ItemDetails item={item} />
        </td>
      </tr>
    )}
  </>
);

const SortIcon = ({ active, dir }: { active: boolean; dir: SortDir }) => (
  <svg className={`w-3 h-3 ${active ? "text-gray-700" : "text-gray-300"}`} fill="currentColor" viewBox="0 0 20 20">
    {dir === "asc" && active ? (
      <path d="M5 13l5-5 5 5H5z" />
    ) : dir === "desc" && active ? (
      <path d="M5 7l5 5 5-5H5z" />
    ) : (
      <>
        <path d="M5 9l5-5 5 5H5z" />
        <path d="M5 11l5 5 5-5H5z" opacity="0.5" />
      </>
    )}
  </svg>
);
